import { gql } from '@apollo/client'
import { v4 as uuid } from 'uuid'
import transl from './useCoverages.transl'

export const groupWorkflowActionsQuery = () => gql`
  query GetGroupWorkflowActions($groupIds: [String!]!) {
    groups(groupIds: $groupIds, includeProperties: true) {
      currentWorkflowActions {
        name
        status
        properties {
          name
          value
        }
      }
      workflowActions {
        name
        status
        properties {
          name
          value
        }
      }
    }
  }
`

export const updateAuditMissingCoveragesAsCompleted = () => gql`
  mutation setAuditMissingCoverageCompleted($params: UpdateWorkflowActionStatusInput) {
    updateWorkflowActionStatus(params: $params) {
      code
      message
    }
  }
`

function isCoverageFullyChecked(coverageCheckboxesAtIndex) {
  return coverageCheckboxesAtIndex?.every((rt) => rt.recordTypeChecked === true)
}

function buildRecordTypeWithDates(coverage, coverageCheckboxes, i, j) {
  let isRecordTypeChecked = false
  const datesToAdd = []

  if (coverageCheckboxes[i] && coverageCheckboxes[i][j]) {
    isRecordTypeChecked = coverageCheckboxes[i][j].recordTypeChecked
    // Dates
    for (let dateIndex = 0; dateIndex < coverageCheckboxes[i][j].datesChecked.length; dateIndex++) {
      // If date is not checked, add the corresponding date to datesToAdd
      if (!coverageCheckboxes[i][j].datesChecked[dateIndex]) {
        datesToAdd.push(coverage.recordTypes[j].date[dateIndex])
      }
    }
  }

  // Ensure at least one date object with a localKey if all dates are deleted
  if (datesToAdd.length < 1) {
    datesToAdd.push({ localKey: uuid() })
  }

  return {
    isRecordTypeChecked,
    datesToAdd,
  }
}

function getFinalCoverageArray(coverages, coverageCheckboxes) {
  const finalCoverageArray = []
  let i = 0
  let j = 0

  // Coverages or Places
  for (i = 0; i < coverages.length; i++) {
    try {
      const isEntireCoverageChecked = isCoverageFullyChecked(coverageCheckboxes[i])
      const coverageRecordTypes = []
      const coverage = coverages[i]

      if (coverageCheckboxes?.length && coverage.recordTypes) {
        // RecordTypes
        for (j = 0; j < coverage.recordTypes.length; j++) {
          const coverageRecordTypeCopy = { ...coverage.recordTypes[j] }
          const { isRecordTypeChecked, datesToAdd } = buildRecordTypeWithDates(coverage, coverageCheckboxes, i, j)
          coverageRecordTypeCopy.date = datesToAdd

          if (!isRecordTypeChecked) coverageRecordTypes.push(coverageRecordTypeCopy)
        }
        coverage.recordTypes = coverageRecordTypes
      }

      if (!isEntireCoverageChecked) finalCoverageArray.push(coverage)
    } catch (error) {
      console.error(`Error at indexes: i = ${i} | j = ${j}`)
      console.error(error)
      console.error(coverages)
      return coverages
    }
  }

  return finalCoverageArray
}

/**
 * Iterates over all the coverages and checks which pieces are checked. If a piece is checked, it will be left out of the new coverages array.
 * @param coverages current coverages array
 * @param coverageCheckboxes Array of coverage checkbox values
 * @return {*[]} new coverages array
 */
export const getCoveragesAfterDelete = (coverages, coverageCheckboxes) => {
  const coveragesCopy = copyCoverages(coverages)
  const finalCoverageArray = getFinalCoverageArray(coveragesCopy, coverageCheckboxes)

  return finalCoverageArray
}

export async function handleAuditMissingCoverages({
  groupId,
  cisId,
  markAuditMissingCoverageCompleted,
  statusOverlay,
  onSuccess,
  onError,
}) {
  if (!groupId) return

  try {
    const response = await markAuditMissingCoverageCompleted({
      variables: {
        params: {
          rmsId: groupId,
          comment: `Reviewed by ${cisId}`,
          newStatus: 'Completed',
          action: 'AuditMissingCoverage',
          createNewWorkflowAction: false,
        },
      },
    })

    const responseSuccessful = response?.data?.updateWorkflowActionStatus?.code === '204'
    statusOverlay({
      message: responseSuccessful
        ? transl.coveragesMarkedCompleteSuccessMessage()
        : transl.coveragesMarkedCompleteFailedMessage(),
      type: responseSuccessful ? 'success' : 'error',
    })

    if (response.error) throw new Error(response.errors)

    if (responseSuccessful && onSuccess) onSuccess()
  } catch (error) {
    console.error(error)
    if (onError) onError(error)
  }
}

export function updateApplyToChildren(coverages, placeIndex, recordTypeIndex, applyToChildren) {
  const coveragesCopy = copyCoverages(coverages)
  const coverageToUpdate = coveragesCopy[placeIndex]

  if (coverageToUpdate) {
    coverageToUpdate.recordTypes.forEach((recordType, i) => {
      if (i === recordTypeIndex) {
        recordType.applyToChildren = applyToChildren
      }
    })
  }

  return coveragesCopy
}

export function updatePlaceCheckbox(coverageCheckboxes, placeIndex, newValue) {
  const newState = [...coverageCheckboxes]

  const newRecordTypesCheckboxes = newState[placeIndex].map((rt) => {
    const newRecordTypeCheckedObj = {}
    newRecordTypeCheckedObj.recordTypeChecked = newValue
    // set date checkbox values
    newRecordTypeCheckedObj.datesChecked = rt.datesChecked.map(() => newValue)
    return newRecordTypeCheckedObj
  })
  newState[placeIndex] = newRecordTypesCheckboxes
  return newState
}

/**
 * Checks the coverages for any of the following errors:
 *    - empty place fields
 *    - duplicate place IDs
 *    - duplicate record type IDs
 * @param {Object[]} coverages
 * @return {*[]} coveragesErrorMap
 */
export function getCoverageErrors(coverages = []) {
  const coverageErrorList = []
  const foundPlaceIds = {}

  if (coverages) {
    coverages.forEach((coverage) => {
      const coverageError = { placeError: null, recordTypeErrors: [], dateErrors: [] }

      // Look for empty place fields or duplicate place IDs
      const placeId = coverage.place?.id
      if (
        placeId &&
        !coverage.place.fullName &&
        !isPlaceholderCoverage(coverage) /* Don't show error for placeholder coverages */
      ) {
        coverageError.placeError = transl.missingPlaceMessage()
      } else if (foundPlaceIds[placeId]) {
        coverageError.placeError = transl.duplicatePlaceError()
      }
      foundPlaceIds[placeId] = true

      // Look for record type errors
      const foundRecordTypeIds = {}
      if (coverage.recordTypes) {
        coverage.recordTypes.forEach((recordType) => {
          // no record type was selected
          if (recordType.name.length === 0) {
            coverageError.recordTypeErrors.push(transl.recordTypeRequiredMessage())
          }
          // duplicate record type
          if (foundRecordTypeIds[recordType.id]) {
            coverageError.recordTypeErrors.push(transl.duplicateRecordTypeError())
          } else {
            coverageError.recordTypeErrors.push(null)
          }
          // recordType date errors
          // the date 1970 comes back as falsy since it's value is 0, so we check for a number rather than false here
          const recordTypeDates = recordType.date

          if (!recordTypeDates?.some((date) => date.formatted || Number.isInteger(date.fromTimestamp))) {
            coverageError.dateErrors.push(transl.dateRequiredMessage())
          } else if (areDatesTooFarInFuture(recordTypeDates)) {
            // date cannot be after next year
            coverageError.dateErrors.push(transl.dateTooFarFutureMessage())
          } else {
            coverageError.dateErrors.push(null)
          }
          foundRecordTypeIds[recordType.id] = true
        })
      }

      coverageErrorList.push(coverageError)
    })
  }

  return coverageErrorList
}

function areDatesTooFarInFuture(dateArray) {
  let result = false
  for (let i = 0; i < dateArray.length; i++) {
    const date = dateArray[i]
    let timestamp = date.fromTimestamp
    if (!timestamp) {
      timestamp = new Date(date.formatted).getTime()
    }

    if (timestamp && timestamp > endOfCurrentYearTimestamp()) {
      result = true
      break
    }
  }
  return result
}

function endOfCurrentYearTimestamp() {
  const date = new Date()
  const currentYear = date.getUTCFullYear()

  return Date.UTC(currentYear, 11, 31, 23, 59, 59, 0)
}

/**
 * When a user adds a place or adds a record type, a placeholder is added with a dummy ID and empty name.
 * This function returns true if a coverage is only made up with a dummy place and dummy record types.
 * @param coverage
 * @return {boolean}
 */
export function isPlaceholderCoverage(coverage) {
  const isValidPlace = Boolean(coverage?.place?.fullName)
  const isValidRecordTypes = Boolean(coverage?.recordTypes?.find((rt) => Boolean(rt.name)))
  return !isValidPlace && !isValidRecordTypes
}

/**
 * When creating a new record type or coverage we need to create a temp id to be able to locate and update the fields
 * @return {number} Returns a negative number so not to conflict with any real ids
 */
export function getRandomId() {
  return -Number(String(Date.now() % Math.random()).slice(-5))
}

export function getEmptyCoverage() {
  return {
    place: {
      fullName: '',
      id: String(getRandomId()),
    },
    recordTypes: [getEmptyRecordType()],
  }
}

export function getEmptyRecordType() {
  return {
    date: [{}],
    id: getRandomId(),
    name: '',
  }
}

/**
 * Finds the record type to be updated
 * @param  {Object[]} coverages     All the current coverages
 * @param  {string}   placeId       ID of place we are adding our recordType
 * @param  {string}   recordTypeId  The id of the record type we want to modify
 * @return {Object}                 The recordType that will be updated
 */
export function findRecordType(coverages, placeId, recordTypeId) {
  const updatedCoverage = coverages.find((coverage) => coverage.place.id === placeId)
  return updatedCoverage.recordTypes.find((rt) => rt.id === recordTypeId)
}

/**
 * Copy the coverages to be modified since it is bad practice to modify the state directly
 * @param  {Object[]} coverages All the current coverages
 * @return {Object[]}           Deep copy of coverages
 */
export function copyCoverages(coverages = []) {
  const coveragesCopy = []
  if (coverages) {
    coverages.forEach((coverage) => {
      const recordTypesCopy = coverage.recordTypes?.map((rt) => {
        const datesCopy = rt.date.map((date) => ({
          ...date,
          localKey: date.localKey || uuid(),
        }))
        return {
          applyToChildren: rt.applyToChildren || false,
          date: datesCopy,
          id: Number(rt.id),
          name: rt.name,
        }
      })

      coveragesCopy.push({
        place: {
          fullName: coverage.place?.fullName,
          id: coverage.place?.id,
          name: coverage?.place?.name,
          ...(coverage.place?.fromYear != null && { fromYear: coverage.place.fromYear }),
          ...(coverage.place?.toYear != null && { toYear: coverage.place.toYear }),
        },
        recordTypes: recordTypesCopy,
      })
    })
  }
  return coveragesCopy
}
